import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogIn, Search, Check, X, Star, BarChart, Calendar, AlertTriangle, PlusCircle, ChevronDown, ChevronUp, Users, BookOpen, Heart, Trash2, Trophy, Clock, Share2, Edit } from 'lucide-react';
import SEO from '../../components/SEO';
import { UserGame } from '../../lib/game/collectionService';
import { GameSession } from '../../lib/game/sessionService';

// Import Hooks
import { usePlayedGamesCollection } from './hooks/usePlayedGamesCollection';
import { useGameSessions } from './hooks/useGameSessions';
import { useGameSessionModal } from './hooks/useGameSessionModal';

// Import View Components
import GamesCollectionView from './components/GamesCollectionView';
import SessionHistoryView from './components/SessionHistoryView';
import StatisticsView from './components/StatisticsView';
import PlayersView from './components/PlayersView';
import GameSessionModal from './components/GameSessionModal';
import SelectGameModal from './components/SelectGameModal';

// Import Services
import { getRankedGames } from '../../lib/game/searchService';
import useDebounce from '../../hooks/useDebounce';
import type { Game as CollectionGame } from '../../lib/game/collectionService';

// Tipos específicos desta página (se necessário)
type ActiveView = 'games' | 'sessions' | 'stats' | 'players';

// Interface para os jogos únicos extraídos das sessões para o filtro de estatísticas
interface UniqueGameForStatsFilter {
  game_id: string;
  name: string | undefined; // Nome do jogo pode ser undefined se não tiver game_info
}

const PlayedGamesPage: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeView, setActiveView] = useState<ActiveView>('games');
  const statsLoadedRef = useRef<{[gameId: string]: boolean}>({});

  // --- Novos Estados para Seleção de Jogo ---
  const [isSelectGameModalOpen, setIsSelectGameModalOpen] = useState(false);
  const [gameSearchTerm, setGameSearchTerm] = useState('');
  const [gameSearchResults, setGameSearchResults] = useState<CollectionGame[]>([]);
  const [isGameSearchLoading, setIsGameSearchLoading] = useState(false);
  const debouncedGameSearchTerm = useDebounce(gameSearchTerm, 400); // Debounce para busca

  // --- Hooks --- 
  const collectionHook = usePlayedGamesCollection();
  const sessionHook = useGameSessions({});
  const modalHook = useGameSessionModal({
    // Callback para atualizar dados após salvar uma sessão
    onSessionSaveSuccess: (sessionId, gameId) => {
      console.log(`[PAGE] Sessão ${sessionId} salva para o jogo ${gameId}. Recarregando...`);
      sessionHook.loadGameSessions(); // Recarrega o histórico
      sessionHook.loadGameSessionStats(gameId); // Recarrega stats do jogo específico
      collectionHook.reloadGames(); // Recarrega a lista de jogos (para play_count)
    },
  });

  // --- Efeitos --- 

  // Carregamento automático de estatísticas para os jogos visíveis
  useEffect(() => {
    if (collectionHook.pagedPlayedGames.length > 0 && user?.id && !collectionHook.loading) {
      // Evitar recarregar as estatísticas em cada renderização
      const unloadedGames = collectionHook.pagedPlayedGames.filter(
        game => game.game_id && 
        !statsLoadedRef.current[game.game_id] && 
        !sessionHook.gameSessionStats[game.game_id]
      );
      
      if (unloadedGames.length === 0) return;
      
      console.log('[PAGE] Carregando estatísticas automaticamente para jogos visíveis não carregados:', 
        unloadedGames.length);
      
      const loadMissingStats = async () => {
        for (const game of unloadedGames) {
          if (game.game_id) {
            try {
              await sessionHook.loadGameSessionStats(game.game_id);
              // Marcar que carregamos as estatísticas deste jogo
              statsLoadedRef.current[game.game_id] = true;
            } catch (error) {
              console.error(`Erro ao carregar estatísticas para jogo ${game.game_id}:`, error);
            }
          }
        }
        console.log('Carregamento automático de estatísticas concluído');
      };

      loadMissingStats();
    }
  }, [collectionHook.pagedPlayedGames, collectionHook.loading, user?.id, sessionHook.loadGameSessionStats]);

  // Carregamento manual de estatísticas para os jogos visíveis (mantém para uso com botão)
  const handleLoadGameStats = useCallback(() => {
    if (collectionHook.pagedPlayedGames.length > 0 && user?.id) {
      const loadMissingStats = async () => {
        console.log('[PAGE] Recarregando todas as estatísticas manualmente para jogos visíveis...');
        const processingIds = new Set<string>();

        for (const game of collectionHook.pagedPlayedGames) {
          if (game.game_id && !processingIds.has(game.game_id)) {
            processingIds.add(game.game_id);
            try {
              await sessionHook.loadGameSessionStats(game.game_id);
              // Atualiza ref para manter controle
              statsLoadedRef.current[game.game_id] = true;
              console.log(`Estatísticas recarregadas para o jogo ${game.game_id}`);
            } catch (error) {
              console.error(`Erro ao carregar estatísticas para jogo ${game.game_id}:`, error);
            }
          }
        }
        console.log('Carregamento manual de estatísticas concluído');
      };
      loadMissingStats();
    }
  }, [collectionHook.pagedPlayedGames, user?.id, sessionHook.loadGameSessionStats]);

  // Verifica parâmetros da URL para abrir modal de registro (lógica mantida)
  useEffect(() => {
    if (isAuthenticated && user && collectionHook.pagedPlayedGames.length > 0) {
      const params = new URLSearchParams(location.search);
      const gameIdParam = params.get('gameId');
      const actionParam = params.get('action');

      if (gameIdParam && actionParam === 'register') {
        const gameToOpen = collectionHook.pagedPlayedGames.find(g => g.game_id === gameIdParam);
        if (gameToOpen) {
          console.log('[PAGE] Abrindo modal de registro via URL para:', gameToOpen);
          modalHook.openModalForNewSession(gameToOpen);
          // Limpa parâmetros da URL
          navigate(location.pathname, { replace: true });
        } else {
            console.warn('[PAGE] Jogo para registro via URL não encontrado na coleção carregada:', gameIdParam);
            // Poderia tentar buscar o jogo individualmente se necessário
        }
      }
    }
  }, [isAuthenticated, user, location.search, navigate, collectionHook.pagedPlayedGames, modalHook.openModalForNewSession]); // Depende dos jogos carregados

  // --- Novo Efeito para Buscar Jogos para Seleção ---
  useEffect(() => {
    const searchGamesForSelection = async () => {
      if (debouncedGameSearchTerm.length < 2) {
        setGameSearchResults([]);
        setIsGameSearchLoading(false);
        return;
      }
      setIsGameSearchLoading(true);
      try {
        // Busca simples por nome, priorizando relevância
        const response = await getRankedGames(
          { sortBy: 'relevance', searchRelevance: true },
          { searchText: debouncedGameSearchTerm },
          1, // Primeira página
          10 // Limitar a 10 resultados para seleção
        );
        setGameSearchResults(response.data);
      } catch (error) {
        console.error("[PAGE] Erro ao buscar jogos para seleção:", error);
        setGameSearchResults([]); // Limpa resultados em caso de erro
        // Poderia mostrar um toast de erro aqui
      } finally {
        setIsGameSearchLoading(false);
      }
    };

    if (isSelectGameModalOpen) { // Só busca se o modal estiver aberto
        searchGamesForSelection();
    }
  }, [debouncedGameSearchTerm, isSelectGameModalOpen]); // Depende do termo debounced e se o modal está aberto

  // --- Handlers --- 
  const handleViewChange = (view: ActiveView) => {
    setActiveView(view);
    // Lógica para carregar dados específicos da view se necessário (ex: se não carregou inicialmente)
    if (view === 'sessions' && sessionHook.gameSessions.length === 0 && !sessionHook.sessionsLoading) {
        sessionHook.loadGameSessions();
    }
    // Outras views (stats, players) carregam seus dados internos ou usam dados já carregados
    // Se a view 'stats' for selecionada e as sessões ainda não foram carregadas, carregue-as
    // pois a lista de jogos para o filtro de estatísticas depende delas.
    if (view === 'stats' && sessionHook.gameSessions.length === 0 && !sessionHook.sessionsLoading) {
      sessionHook.loadGameSessions();
    }
  };

  const handleAddSessionFromList = (game: UserGame) => {
    modalHook.openModalForNewSession(game);
  };

  const handleEditSessionFromList = (session: GameSession) => {
      const gameInfo = sessionHook.gameSessions.find(s => s.id === session.id)?.game;
      modalHook.openModalForEditSession(session, gameInfo); // Passa a sessão e opcionalmente o jogo já formatado
  };

  const handleAddSessionGeneral = () => {
    setGameSearchTerm(''); // Limpa busca anterior
    setGameSearchResults([]); // Limpa resultados anteriores
    setIsSelectGameModalOpen(true); // Abre o modal de seleção
    // O alert antigo foi removido
  };

  // --- Novo Handler para quando um jogo é selecionado no modal de seleção ---
  const handleGameSelectedForSession = (game: CollectionGame) => {
    setIsSelectGameModalOpen(false);

    // Cria um objeto UserGame (importado na linha 6)
    const userGame: UserGame = {
        id: `temp-${game.id}`, 
        user_id: user?.id || '',
        game_id: game.id,
        owned: false,
        wishlist: false,
        rating: null,
        play_count: 0,
        notes: null,
        created_at: '', 
        updated_at: '',
        complexity_rating: null,
        game: game // Aninha o objeto CollectionGame diretamente
    };

    console.log('[PAGE] Jogo selecionado para nova sessão:', userGame);
    // modalHook.openModalForNewSession espera UserGame
    modalHook.openModalForNewSession(userGame); 
  };

  // Extrair jogos únicos das sessões para o filtro da StatisticsView
  const uniqueGamesForStatsFilter = React.useMemo(() => {
    if (!sessionHook.gameSessions || sessionHook.gameSessions.length === 0) {
      return [];
    }
    const gamesMap = new Map<string, UniqueGameForStatsFilter>();
    sessionHook.gameSessions.forEach(session => {
      if (session.game_id && !gamesMap.has(session.game_id)) {
        gamesMap.set(session.game_id, {
          game_id: session.game_id,
          name: session.game?.name || 'Jogo Desconhecido' 
        });
      }
    });
    return Array.from(gamesMap.values()).sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [sessionHook.gameSessions]);

  // --- Renderização --- 

  // Renderiza o estado de "Não autenticado"
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="container mx-auto max-w-6xl py-8">
        <div className="text-center py-16 dark:bg-gray-800 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">Você precisa estar conectado</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Entre na sua conta para gerenciar seus jogos e partidas.</p>
          <button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg inline-flex items-center transition duration-150 ease-in-out">
            <LogIn className="h-5 w-5 mr-2" /> Fazer Login
          </button>
        </div>
      </div>
    );
  }

  // Renderiza o estado de carregamento inicial (auth ou hooks principais)
   if (authLoading || (collectionHook.loading && collectionHook.pagedPlayedGames.length === 0)) {
       return (
           <div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8 text-center text-gray-700 dark:text-gray-300">
               <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 dark:border-indigo-400 mx-auto mb-4"></div>
               Carregando dados...
           </div>
       );
   }

  // Renderiza o conteúdo principal da página
  return (
    <>
      <SEO
        title="Meus Jogos Jogados"
        description="Registre e acompanhe os jogos de tabuleiro que você já jogou."
      />
      <div className="container mx-auto max-w-7xl py-6 px-1 sm:px-3 md:px-4 lg:px-6 dark:bg-gray-900">
        {/* Cabeçalho */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">Meus Jogos Jogados</h1>
          {/* <button onClick={() => navigate('/search')} className="bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-3 sm:py-2 sm:px-4 rounded-lg inline-flex items-center transition duration-150 ease-in-out text-sm sm:text-base">
            <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" /> <span className="sm:inline">Adicionar/Avaliar</span>
          </button> */}
        </div>

        {/* Diagnóstico Admin (se aplicável) */} 
        {isAdmin && collectionHook.diagnosticInfo && (
             <div className="bg-yellow-50 dark:bg-yellow-900/70 border border-yellow-200 dark:border-yellow-700 p-3 rounded-lg mb-4">
               <h3 className="font-semibold flex items-center mb-2 text-yellow-700 dark:text-yellow-300">
                 <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600 dark:text-yellow-400" />
                 Diagnóstico (Admin)
               </h3>
               <div className="text-xs font-mono overflow-x-auto p-2 bg-black dark:bg-gray-800 text-green-400 dark:text-green-300 rounded">{collectionHook.diagnosticInfo}</div>
             </div>
        )}

        {/* Navegação por Abas */} 
        <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
          <nav className="-mb-px flex space-x-0 sm:space-x-1 overflow-x-auto scrollbar-hide pb-1">
            <button
              onClick={() => handleViewChange('games')}
              className={`${activeView === 'games' ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'} whitespace-nowrap py-2 px-1.5 sm:py-3 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center`}
            >
              <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" /> <span>Coleção</span>
            </button>
            <button
              onClick={() => handleViewChange('sessions')}
              className={`${activeView === 'sessions' ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'} whitespace-nowrap py-2 px-1.5 sm:py-3 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center`}
            >
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" /> <span>Histórico</span>
            </button>
            <button
              onClick={() => handleViewChange('stats')}
              className={`${activeView === 'stats' ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'} whitespace-nowrap py-2 px-1.5 sm:py-3 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center`}
            >
              <BarChart className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" /> <span>Estatísticas</span>
            </button>
            <button
              onClick={() => handleViewChange('players')}
              className={`${activeView === 'players' ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'} whitespace-nowrap py-2 px-1.5 sm:py-3 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center`}
            >
              <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" /> <span>Jogadores</span>
            </button>
          </nav>
        </div>

        {/* Renderização Condicional da View Ativa */} 
        <div className="mt-4">
          {activeView === 'games' && (
            <>
              <div className="mb-4 flex justify-end">
                <button 
                  onClick={handleLoadGameStats}
                  className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm flex items-center"
                >
                  <BarChart className="h-4 w-4 mr-1" />
                  Carregar Estatísticas
                </button>
              </div>
              <GamesCollectionView 
                collectionHook={collectionHook} 
                onAddSession={handleAddSessionFromList} 
                onAddSessionGeneral={handleAddSessionGeneral}
                gameSessionStats={sessionHook.gameSessionStats}
              />
            </>
          )}
          {activeView === 'sessions' && user && (
            <SessionHistoryView 
                userId={user.id}
                playedGames={collectionHook.pagedPlayedGames} // Passa a lista de jogos para o filtro
                sessionHook={sessionHook} 
                onEditSession={handleEditSessionFromList}
                onAddSessionClick={handleAddSessionGeneral}
            />
          )}
           {activeView === 'stats' && user && (
            <StatisticsView 
                userId={user.id}
                // Passa a lista de jogos únicos extraídos das sessões
                gamesForFilter={uniqueGamesForStatsFilter}
            />
          )}
          {activeView === 'players' && user && (
            <PlayersView userId={user.id} />
          )}
        </div>

        {/* Modal de Sessão (controlado pelo hook) */} 
        <GameSessionModal
          isOpen={modalHook.sessionModalOpen}
          isEditing={!!modalHook.editingSessionId}
          isSaving={modalHook.isSaving}
          formData={modalHook.sessionFormData}
          selectedGame={modalHook.selectedGame}
          availablePlayers={modalHook.availablePlayers}
          selectedPlayers={modalHook.selectedPlayers}
          winnerPlayerIds={modalHook.winnerPlayerIds}
          modalRef={modalHook.modalRef}
          onClose={modalHook.handleCloseModal}
          onChange={modalHook.handleSessionFormChange}
          onPlayerSelect={modalHook.handlePlayerSelection}
          onTogglePlayerWinner={modalHook.handleTogglePlayerWinner}
          onSubmit={modalHook.handleSaveSession}
        />

        {/* <<< Novo Modal de Seleção de Jogo >>> */}
        <SelectGameModal
          isOpen={isSelectGameModalOpen}
          isLoading={isGameSearchLoading}
          searchTerm={gameSearchTerm}
          searchResults={gameSearchResults}
          onClose={() => setIsSelectGameModalOpen(false)}
          onSearchTermChange={setGameSearchTerm}
          onGameSelect={handleGameSelectedForSession}
        />
      </div>
    </>
  );
};

export default PlayedGamesPage; 